import moment from 'moment';
import React, { useEffect, useState } from 'react';

import BookCardHome from '@/components/BundleComp/BookCard';
import BookRankCard from '@/components/BundleComp/BookRankCard';
import SkeletonCard from '@/components/shared/SkeletonCard';

import { getAllBundleListApi } from '@/services/Library/Bundle';

import { ShouldRender } from '../shared/basic/ShouldRender';
import { getLocalUserId, getZl_UID } from '../shared/getToken';
import { notify } from '../shared/notify';
import HomepageSlider from '../V2/HomePage/HomepageSlider';
import { Endpoints } from '../../network';
import { generateUrlForImage } from '../../pages/books/[bookId]';
import { getAllRecentlyViewedBooksApi } from '../../services/Library/Books';
import { PaginationProps } from '../../utils/interfaces/Orders';

interface Props {
  categoryName: string;
  categoryId: string | null;
  subHeading?: string;
  subHeadingCol?: string;
  subHeadingLink?: string;
  search?: string;
  bookType?: string;
  isHomePage?: boolean;
  categoryIdFilter?: string | null;
  order?: string;
  featured?: boolean;
  isRecentlyViewed?: boolean;
  includeCategories?: string;
  notBookLandingPage?: boolean;
  endDateFilter?: string;
  category_name?: string;
  isTopTen?: boolean;
  hideShortBook?: boolean;
  isAddToCart?: boolean;
}

const PaginatedBundleSlider: React.FC<Props> = ({
  categoryId,
  categoryName,
  search,
  isHomePage = false,
  bookType,
  categoryIdFilter,
  order,
  featured,
  isRecentlyViewed = false,
  includeCategories,
  notBookLandingPage = true,
  endDateFilter,
  category_name,
  subHeading,
  subHeadingLink,
  subHeadingCol,
  isTopTen,
  hideShortBook = false,
  isAddToCart,
}) => {
  const [books, setBooks] = useState<
    {
      _id: string;
      coverImageUrl: string;
      title: string;
      externalLink: string;
      type: string;
      slug: string;
      totalPages: number;
      isExternalLink: boolean;
      author_name?: string;
      tag?: string;
      isFree?: boolean;
      topTen?: string;
      bookCount?: number;
      courseCount?: number;
      main_price?: number;
      stroked_price?: number;
    }[]
  >([]);
  const [page, setPage] = useState<number>(1);
  const [loading, setLoading] = useState(true);
  const [isPaginateShift, setIsPaginateShift] = useState(true);
  const [paginationData, setPaginationData] = useState<PaginationProps>();
  const handleGetAllBooks = async () => {
    try {
      setLoading(true);
      const response = await getAllBundleListApi({
        page: page.toString(),
        categoriesId: categoryId,

        keyword: search,
        ...(isHomePage && bookType && { type: bookType }),
        ...(bookType === 'comingSoon' && { type: bookType }),
        ...(hideShortBook && { type: 'long' }),
        ...(category_name && { category_name }),
        ...(categoryIdFilter && { categoryId: categoryIdFilter as string }),
        ...(order && { order }),
        ...(featured && { featured }),
        ...(includeCategories && { includeCategories }),
        ...(isTopTen && { topRankingOnly: true }),
        ...(endDateFilter &&
          typeof endDateFilter !== undefined && { end_date: endDateFilter }),
      });
      const { data } = response;

      const formattedBooks = data?.map((book: any) => {
        // let tag: string | null = null;

        if (book?.type === 'comingSoon') {
          // tag = 'COMING SOON';
        } else if (
          moment(book.createdAt).isAfter(moment().subtract(2, 'months'))
        ) {
          // tag = 'New';
        } else if (book?.featured) {
          // tag = 'BESTSELLER';
        }
        return {
          _id: book?._id,
          coverImageUrl: generateUrlForImage(
            `${process.env.NEXT_PUBLIC_API_READER_URL}/`,
            `${Endpoints.getCoverImagesForBundle}/`,
            `${book?._id}`,
          ),
          title: book?.title,
          externalLink: book?.externalLink,
          slug: book?.slug,
          type: book?.type,
          isExternalLink: book?.isExternalLink,
          // totalPages: book?.totalPages,
          // author_name: book?.author_list[0]?.name,
          // tag,
          isFree: book?.type === 'short',
          topTen: book?.topRanking,
          bookCount: book?.bookCount,
          courseCount: book?.courseCount,
          main_price: book?.main_price,
          stroked_price: book?.stroked_price,
        };
      });

      setPaginationData({
        current_page: response?.metadata?.page,
        per_page: response?.metadata?.limit,
        total: response?.metadata?.total,
      });

      if (page === 1) {
        setBooks(formattedBooks);
      } else {
        setBooks((prevData) => [...prevData, ...formattedBooks]);
      }
    } catch (error: any) {
      notify(error, 'error');
    } finally {
      setLoading(false);
    }
  };

  const handleGetRecentlyViewedBooks = async () => {
    try {
      setLoading(true);
      const [zl_uid, localUserId] = await Promise.all([
        getZl_UID(),
        getLocalUserId(),
      ]);
      const response = await getAllRecentlyViewedBooksApi({
        page: page.toString(),
        keyword: search,
        zl_uid: zl_uid,
        tempUserId: localUserId as string,
      });
      const { data } = response;

      const formattedBooks = data?.map((book: any) => ({
        _id: book?.product?._id,
        coverImageUrl: generateUrlForImage(
          `${process.env.NEXT_PUBLIC_API_READER_URL}/`,
          `${Endpoints.getCoverImagesForBooks}/`,
          `${book?.product?._id}`,
        ),
        title: book?.product?.title,
        externalLink: book?.product?.externalLink,
        slug: book?.product?.slug,
        type: book?.product?.type,
        isExternalLink: book?.product?.isExternalLink,
        totalPages: book?.product?.totalPages,
      }));

      setPaginationData({
        current_page: response?.metadata?.page,
        per_page: response?.metadata?.limit,
        total: response?.metadata?.total,
      });

      if (page === 1) {
        setBooks(formattedBooks);
      } else {
        setBooks((prevData) => [...prevData, ...formattedBooks]);
      }
    } catch (error: any) {
      notify(error, 'error');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    if (isRecentlyViewed) {
      handleGetRecentlyViewedBooks();
    } else {
      handleGetAllBooks();
    }
  }, [page, categoryId, search, categoryIdFilter, isRecentlyViewed]);

  const handlePagination = () => {
    if (paginationData && books?.length < paginationData?.total)
      setPage((prevPage) => prevPage + 1);
  };

  return (
    <>
      {loading || books?.length > 0 ? (
        <HomepageSlider
          parantClassName='  md:gap-x-2 sm:gap-x-2 gap-x-2'
          heading={categoryName}
          onPagination={handlePagination}
          setIsPaginateShift={setIsPaginateShift}
          isPaginateShift={isPaginateShift}
          loading={!loading && !!books?.length}
          total={paginationData?.total || 0}
          imageWidths={isTopTen ? [112, 193, 210] : [120, 140, 168]}
          notBookLandingPage={notBookLandingPage}
          subHeading={subHeading}
          subHeadingLink={subHeadingLink}
          subHeadingCol={subHeadingCol}
        >
          {loading
            ? Array.from({ length: 10 }).map((_, index) => (
              <SkeletonCard
                variant={isTopTen ? 'rank' : 'home'}
                key={index}
                index={index}
              />
            ))
            : books?.map((item, index) => (
              <span
                key={`${item._id}-${index}`}
                className='group cursor-pointer  no-select flex p-1'
              >
                <ShouldRender check={isTopTen}>
                  <BookRankCard
                    book={{
                      coverImage: item.coverImageUrl,
                      id: item._id,
                      rank: +(item.topTen as string),
                      slug: item.slug,
                      title: item.title,
                      externalLink: item.externalLink,
                      isExternalLink: item.isExternalLink,
                    }}
                  />
                </ShouldRender>
                <ShouldRender check={!isTopTen}>
                  <BookCardHome
                    isAddToCart={isAddToCart}
                    book={{
                      coverImage: item.coverImageUrl,
                      id: item._id,
                      slug: item.slug,
                      title: item.title,
                      tag: item.tag,
                      topTen: item.topTen,
                      isFree: item.isFree as boolean,
                      externalLink: item.externalLink,
                      isExternalLink: item.isExternalLink,
                      courseCount: item.courseCount,
                      bookCount: item.bookCount,
                      main_price: item?.main_price,
                      stroked_price: item?.stroked_price,
                    }}
                    notBookLandingPage={notBookLandingPage}
                  />
                </ShouldRender>
              </span>
            ))}
          <ShouldRender check={loading || isPaginateShift}>
            {Array.from({ length: 10 }).map((_, index) => (
              <SkeletonCard
                variant={isTopTen ? 'rank' : 'home'}
                key={index}
                index={index}
              />
            ))}
          </ShouldRender>
        </HomepageSlider>
      ) : null}
    </>
  );
};

export default PaginatedBundleSlider;
